/**
 * One place that decides whether a role string matches a gate.
 *
 * Why this exists
 * ---------------
 * Migration `0036` (2026-08-19) renamed the `CAMPAIGN_MANAGER` role to
 * `SMS_MANAGER`. The rename reached the backend and `NavConfig.ts` — which
 * keys off lowercase wire values — but NOT the seven view-level gates, each of
 * which held its own `new Set([... 'CAMPAIGN_MANAGER' ...])` and tested
 * `userRole.toUpperCase()` against it.
 *
 * `/auth/me` returns `role: "sms_manager"`, which uppercases to `SMS_MANAGER`
 * and matched none of them. The result was the worst-looking possible bug: the
 * menu item appeared (NavConfig had been updated) and every view behind it
 * denied access. An `sms_manager` account was locked out of Campaigns, the
 * composer, campaign detail, the messenger queue, the mail-list builder, the
 * channel planner, and compliance — all seven of its own surfaces.
 *
 * Seven copies of a role list is what made a one-line rename into a
 * seven-file, silently-broken change. The backend hit the same problem from
 * the other direction and answered it with `services/provisioning.py` — one
 * fence, many callers. This is the frontend's equivalent for role gates.
 *
 * Wire values are lowercase (`sms_manager`); these gates are written in
 * SCREAMING_CASE. `roleMatches` normalizes and applies the alias table, so
 * callers can keep whichever spelling reads better at the call site.
 */

/**
 * Roles that are the same authority under two names.
 *
 * `CAMPAIGN_MANAGER` -> `SMS_MANAGER` is the 0036 rename. The backend's
 * `UserRole._missing_` still accepts the legacy value on INPUT, so a token or
 * an integration minted before the rename keeps working; this table is the
 * mirror of that leniency on the read side.
 *
 * `MANAGER` -> `ORG_MANAGER` is the older three-role-model alias, kept for the
 * same reason and documented on `UserRole` in `backend/app/models/user.py`.
 */
const ROLE_ALIASES: Record<string, string> = {
  CAMPAIGN_MANAGER: 'SMS_MANAGER',
};

/** Uppercase, trim, and resolve aliases to the canonical name. */
export function canonicalRole(role: string | null | undefined): string {
  if (typeof role !== 'string') return '';
  const upper = role.trim().toUpperCase();
  return ROLE_ALIASES[upper] ?? upper;
}

/**
 * Does `role` satisfy a gate?
 *
 * Both sides are canonicalized, so a gate written as `CAMPAIGN_MANAGER` and a
 * user whose role is `sms_manager` match — which is the whole point. Prefer
 * writing new gates with the canonical name.
 *
 *     const ALLOWED = ['ADMIN', 'ORG_MANAGER', 'SMS_MANAGER'];
 *     if (!roleMatches(user?.role, ALLOWED)) return <AccessDenied />;
 */
export function roleMatches(
  role: string | null | undefined,
  allowed: Iterable<string>,
): boolean {
  const canon = canonicalRole(role);
  if (!canon) return false;
  for (const a of allowed) {
    if (canonicalRole(a) === canon) return true;
  }
  return false;
}
